/**
 * File Browser Component
 *
 * Tabbed file management interface:
 * - Generated Files tab: workflow-generated files grouped into collapsible boxes by run
 * - Uploads tab: user-uploaded files in collapsible cards with drag-and-drop
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Upload,
  Download,
  Trash2,
  File,
  FileText,
  FileImage,
  FileVideo,
  FileArchive,
  FileCode,
  FileSpreadsheet,
  Music,
  RefreshCw,
  Search,
  X,
  AlertCircle,
  Loader,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  Package,
} from 'lucide-react';
import { listFiles, smartUpload, downloadFile, deleteFile, listFileUsage } from '../api/filesApi';
import { getWorkflowIdentifier } from '../utils/workflowIdentifier';

// --- Utility functions ---

const getFileIcon = (mimeType, filename) => {
  if (!mimeType && filename) {
    const ext = filename.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return FileImage;
    if (['mp4', 'avi', 'mov', 'mkv'].includes(ext)) return FileVideo;
    if (['zip', 'rar', 'tar', 'gz', '7z', 'bz2'].includes(ext)) return FileArchive;
    if (['js', 'jsx', 'py', 'java', 'cpp', 'c', 'html', 'css', 'json'].includes(ext)) return FileCode;
    if (['mp3', 'wav', 'flac', 'ogg', 'm4a'].includes(ext)) return Music;
    if (['txt', 'md', 'pdf', 'doc', 'docx'].includes(ext)) return FileText;
    if (['xls', 'xlsx', 'csv', 'tsv'].includes(ext)) return FileSpreadsheet;
  }
  if (mimeType) {
    if (mimeType.startsWith('image/')) return FileImage;
    if (mimeType.startsWith('video/')) return FileVideo;
    if (mimeType.startsWith('audio/')) return Music;
    if (mimeType.startsWith('text/')) return FileText;
    if (mimeType.includes('zip') || mimeType.includes('archive')) return FileArchive;
    if (mimeType.includes('application/json') || mimeType.includes('javascript')) return FileCode;
  }
  return File;
};

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
};

// --- FileRow: a single file inside a workflow group ---

function FileRow({ file, onDownload, onDelete }) {
  const FileIcon = getFileIcon(file.mime_type, file.filename);
  return (
    <div className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
      <FileIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900 truncate">{file.filename}</div>
        <div className="text-xs text-gray-500">
          {formatFileSize(file.file_size)} · {formatDate(file.created_at)}
          {file.workflow_node_key && (
            <span className="ml-1 text-gray-400">· {file.workflow_node_key}</span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          onClick={() => onDownload(file)}
          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
          title="Download"
        >
          <Download className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => onDelete(file)}
          className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
          title="Delete"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// --- WorkflowGroup: collapsible box for all files from one workflow run ---

function WorkflowGroup({ workflowId, workflowName, files, onDownload, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const label = workflowName
    || (workflowId ? getWorkflowIdentifier({ id: workflowId }) : 'Ungrouped');

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden mb-2">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-white hover:bg-gray-50 transition-colors text-left"
      >
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
        )}
        {workflowId ? (
          <FolderOpen className="w-4 h-4 text-blue-500 flex-shrink-0" />
        ) : (
          <Package className="w-4 h-4 text-gray-400 flex-shrink-0" />
        )}
        <span className="flex-1 font-mono text-sm font-medium text-gray-900 truncate">
          {label}
        </span>
        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full flex-shrink-0">
          {files.length} file{files.length !== 1 ? 's' : ''}
        </span>
      </button>
      {expanded && (
        <div className="divide-y divide-gray-100 border-t border-gray-200">
          {files.map((file) => (
            <FileRow
              key={file.file_id}
              file={file}
              onDownload={onDownload}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// --- UploadFileCard: collapsible card for a user-uploaded file ---

function UploadFileCard({ file, onDownload, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [usages, setUsages] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const FileIcon = getFileIcon(file.mime_type, file.filename);

  const handleExpand = useCallback(async () => {
    const willExpand = !expanded;
    setExpanded(willExpand);
    if (willExpand && usages === null) {
      setUsageLoading(true);
      try {
        const data = await listFileUsage(file.file_id);
        setUsages(data.usages || []);
      } catch {
        setUsages([]);
      } finally {
        setUsageLoading(false);
      }
    }
  }, [expanded, usages, file.file_id]);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden mb-2">
      <div className="flex items-center gap-3 px-4 py-3 bg-white hover:bg-gray-50">
        <button
          onClick={handleExpand}
          className="flex items-center gap-3 flex-1 min-w-0 text-left"
        >
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
          )}
          <FileIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-900 truncate">{file.filename}</div>
            <div className="text-xs text-gray-500">
              {formatFileSize(file.file_size)} · {formatDate(file.created_at)}
            </div>
          </div>
        </button>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={() => onDownload(file)}
            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
            title="Download"
          >
            <Download className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onDelete(file)}
            className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
            title="Delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {expanded && (
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            Used in workflows
          </div>
          {usageLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Loader className="w-3.5 h-3.5 animate-spin" />
              Loading...
            </div>
          ) : usages && usages.length > 0 ? (
            <ul className="space-y-1">
              {usages.map((u, idx) => (
                <li key={idx} className="text-sm text-gray-700 flex items-center gap-2">
                  <Package className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span className="truncate">
                    {getWorkflowIdentifier({ id: u.workflow_id })}
                  </span>
                  <span className="text-xs text-gray-400 flex-shrink-0">
                    {u.role}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-gray-400 italic">
              Not used in any workflows yet
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// --- Main FileBrowser component ---

export default function FileBrowser() {
  const [activeTab, setActiveTab] = useState('outputs');
  const [outputFiles, setOutputFiles] = useState([]);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [outputsLoaded, setOutputsLoaded] = useState(false);
  const [uploadsLoaded, setUploadsLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingFile, setUploadingFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const fileInputRef = useRef(null);

  const loadOutputs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await listFiles({ limit: 200, file_type: 'output' });
      setOutputFiles(response.files || []);
      setOutputsLoaded(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUploads = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await listFiles({ limit: 200, file_type: 'upload' });
      setUploadFiles(response.files || []);
      setUploadsLoaded(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load active tab's data on first activation
  useEffect(() => {
    if (activeTab === 'outputs' && !outputsLoaded) loadOutputs();
    if (activeTab === 'uploads' && !uploadsLoaded) loadUploads();
  }, [activeTab, outputsLoaded, uploadsLoaded, loadOutputs, loadUploads]);

  // Always load outputs on mount
  useEffect(() => {
    loadOutputs();
  }, [loadOutputs]);

  const handleRefresh = () => {
    if (activeTab === 'outputs') {
      setOutputsLoaded(false);
      loadOutputs();
    } else {
      setUploadsLoaded(false);
      loadUploads();
    }
  };

  // Group output files by workflow_id, sorted newest first (ungrouped last)
  const outputGroups = useMemo(() => {
    const filtered = searchQuery
      ? outputFiles.filter((f) =>
          f.filename.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : outputFiles;

    const groupMap = new Map();
    for (const file of filtered) {
      const key = file.workflow_id || '__ungrouped__';
      if (!groupMap.has(key)) {
        groupMap.set(key, {
          workflowId: file.workflow_id || null,
          workflowName: file.workflow_name || null,
          files: [],
          latestDate: file.created_at || '',
        });
      }
      const g = groupMap.get(key);
      g.files.push(file);
      if ((file.created_at || '') > g.latestDate) g.latestDate = file.created_at;
    }

    return Array.from(groupMap.values()).sort((a, b) => {
      if (!a.workflowId) return 1;
      if (!b.workflowId) return -1;
      return (b.latestDate || '').localeCompare(a.latestDate || '');
    });
  }, [outputFiles, searchQuery]);

  // Filter uploads by search query
  const filteredUploads = useMemo(() => {
    if (!searchQuery) return uploadFiles;
    return uploadFiles.filter((f) =>
      f.filename.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [uploadFiles, searchQuery]);

  // --- Upload handlers ---

  const handleFileUpload = async (fileToUpload) => {
    if (!fileToUpload) return;
    setUploading(true);
    setUploadingFile(fileToUpload.name);
    setUploadProgress(0);
    setError(null);
    try {
      await smartUpload(fileToUpload, (percent, uploadedChunks, totalChunks) => {
        setUploadProgress(percent);
        if (totalChunks) {
          setUploadingFile(`${fileToUpload.name} (${uploadedChunks}/${totalChunks} chunks)`);
        }
      });
      setUploadsLoaded(false);
      await loadUploads();
      setUploadingFile(null);
      setUploadProgress(0);
    } catch (err) {
      setError(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFileUpload(file);
  };

  // --- Download / delete handlers ---

  const handleDownload = async (file) => {
    try {
      await downloadFile(file.file_id, file.filename);
    } catch (err) {
      setError(`Download failed: ${err.message}`);
    }
  };

  const handleDelete = (file) => {
    setSelectedFile(file);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!selectedFile) return;
    try {
      await deleteFile(selectedFile.file_id);
      // Remove from the appropriate list
      if (selectedFile.file_type === 'output') {
        setOutputFiles((prev) => prev.filter((f) => f.file_id !== selectedFile.file_id));
      } else {
        setUploadFiles((prev) => prev.filter((f) => f.file_id !== selectedFile.file_id));
      }
      setShowDeleteConfirm(false);
      setSelectedFile(null);
    } catch (err) {
      setError(`Delete failed: ${err.message}`);
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Files</h1>
            <p className="text-sm text-gray-600 mt-1">
              Manage uploaded files and workflow outputs
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              onClick={() => {
                setActiveTab('uploads');
                fileInputRef.current?.click();
              }}
              disabled={uploading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload className="w-4 h-4" />
              <span>Upload File</span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileInputChange}
              className="hidden"
            />
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search files..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 px-6">
        <div className="flex gap-1">
          {[
            { id: 'outputs', label: 'Generated Files', count: outputFiles.length, loaded: outputsLoaded },
            { id: 'uploads', label: 'Uploads', count: uploadFiles.length, loaded: uploadsLoaded },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
              {tab.loaded && (
                <span
                  className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${
                    activeTab === tab.id
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Upload progress */}
      {uploading && (
        <div className="bg-blue-50 border-b border-blue-200 px-6 py-3">
          <div className="flex items-center gap-3">
            <Loader className="w-5 h-5 text-blue-600 animate-spin" />
            <div className="flex-1">
              <div className="text-sm font-medium text-blue-900">
                Uploading: {uploadingFile}
              </div>
              <div className="mt-1 bg-blue-200 rounded-full h-1.5 overflow-hidden">
                <div
                  className="bg-blue-600 h-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <div className="text-xs text-blue-700 mt-0.5">{uploadProgress}% complete</div>
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border-b border-red-200 px-6 py-3">
          <div className="flex items-center gap-2 text-red-800">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-600 hover:text-red-800"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <div
        className="flex-1 overflow-auto px-6 py-4"
        onDragEnter={activeTab === 'uploads' ? handleDrag : undefined}
        onDragLeave={activeTab === 'uploads' ? handleDrag : undefined}
        onDragOver={activeTab === 'uploads' ? handleDrag : undefined}
        onDrop={activeTab === 'uploads' ? handleDrop : undefined}
      >
        {/* Drag overlay (uploads tab only) */}
        {dragActive && activeTab === 'uploads' && (
          <div className="fixed inset-0 bg-blue-500 bg-opacity-10 border-4 border-dashed border-blue-500 z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-xl p-8 text-center">
              <Upload className="w-16 h-16 text-blue-600 mx-auto mb-4" />
              <p className="text-xl font-semibold text-gray-900">Drop file to upload</p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <Loader className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        ) : activeTab === 'outputs' ? (
          /* Outputs tab: grouped by workflow run */
          outputGroups.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-gray-500">
              <FolderOpen className="w-12 h-12 mb-3 text-gray-300" />
              <p className="text-base font-medium">
                {searchQuery ? 'No files match your search' : 'No workflow output files yet'}
              </p>
              {!searchQuery && (
                <p className="text-sm mt-1 text-gray-400">
                  Run a workflow to generate output files
                </p>
              )}
            </div>
          ) : (
            <div>
              {outputGroups.map((group) => (
                <WorkflowGroup
                  key={group.workflowId || '__ungrouped__'}
                  workflowId={group.workflowId}
                  workflowName={group.workflowName}
                  files={group.files}
                  onDownload={handleDownload}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )
        ) : (
          /* Uploads tab: drag-drop area + per-file collapsed cards */
          <div>
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-lg p-6 mb-4 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
            >
              <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-700">
                Click or drag &amp; drop to upload
              </p>
              <p className="text-xs text-gray-500 mt-1">Any file type supported</p>
            </div>

            {filteredUploads.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-gray-500">
                <File className="w-10 h-10 mb-2 text-gray-300" />
                <p className="text-sm font-medium">
                  {searchQuery ? 'No uploads match your search' : 'No uploaded files yet'}
                </p>
              </div>
            ) : (
              filteredUploads.map((file) => (
                <UploadFileCard
                  key={file.file_id}
                  file={file}
                  onDownload={handleDownload}
                  onDelete={handleDelete}
                />
              ))
            )}
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && selectedFile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete File</h3>
            </div>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete{' '}
              <strong>{selectedFile.filename}</strong>? This action cannot be undone.
            </p>
            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setSelectedFile(null);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
